/**
 * Chronological ordering for rostered shifts.
 *
 * Exists because comparing ISO timestamps as TEXT is a live defect class in roster code.
 * `2026-07-20T09:00:00Z` and `2026-07-20T09:00:00+00:00` are the same instant, but `'+'`
 * (0x2B) sorts before `'Z'` (0x5A), so a text sort silently reorders a worker's day. The
 * consequences are not cosmetic: it changes which hop a travel estimate measures, which
 * pair a rest gap is measured across, and the order an ordinary-hours accumulator sees.
 *
 * Kept in one place for the same reason the travel thresholds are — two copies of an
 * ordering rule drift, and the drift is invisible until a roster reads wrong.
 */

/**
 * Compares two timestamps by parsed instant.
 *
 * Unparseable values sort last and compare equal to each other, so a bad row lands at the
 * end of the day rather than at an arbitrary position that shifts as neighbours change.
 * Callers that need total determinism should chain a stable tiebreak (an id or key).
 */
export function compareInstants(
  a: string | null | undefined,
  b: string | null | undefined,
): number {
  const aMs = a == null ? Number.NaN : Date.parse(a);
  const bMs = b == null ? Number.NaN : Date.parse(b);
  const aValid = Number.isFinite(aMs);
  const bValid = Number.isFinite(bMs);

  if (aValid && bValid) return aMs === bMs ? 0 : aMs - bMs;
  if (aValid !== bValid) return aValid ? -1 : 1;
  return 0;
}

/**
 * Newest-first ordering, keeping unparseable values LAST.
 *
 * Exists because `compareInstants(b, a)` is the obvious way to write a
 * descending sort and is WRONG. The "unparseable last" guarantee above is
 * expressed relative to the argument positions, so flipping the arguments also
 * flips it: a null or empty timestamp floats to the HEAD of the list and wins
 * any "pick the most recent" selection.
 *
 * The idiom this replaces — `String(b.x ?? '').localeCompare(String(a.x ?? ''))`
 * — did NOT have that problem, because `''` is lexicographically smallest and
 * therefore landed last in a descending text sort. So a naive conversion to
 * `compareInstants(b, a)` silently changes which row wins, which is a behaviour
 * regression hiding inside what looks like a pure correctness fix. Measured on
 * `roster-gate-detection.ts` work-rights selection: a record with a null
 * `updatedAt` went from sorting last to sorting first, i.e. becoming the record
 * the BLOCK gate judges the worker on.
 *
 * Use this for every newest-first ordering. For oldest-first, use
 * `compareInstants` directly.
 */
export function compareInstantsDesc(
  a: string | null | undefined,
  b: string | null | undefined,
): number {
  const aMs = a == null ? Number.NaN : Date.parse(a);
  const bMs = b == null ? Number.NaN : Date.parse(b);
  const aValid = Number.isFinite(aMs);
  const bValid = Number.isFinite(bMs);

  // Validity dominates direction: a bad row is last whichever way time runs.
  if (aValid !== bValid) return aValid ? -1 : 1;
  if (!aValid) return 0;
  return aMs === bMs ? 0 : bMs - aMs;
}
